import { Fragment, useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeftRight, Check, Lock, MoreHorizontal, Plus, RotateCcw, Trash2, UserMinus, UserPlus, UserRoundCheck } from "lucide-react"

import { api } from "../api"
import { ConfirmDialog, LoadingCard, messageFor, Page } from "../shared"
import { fa, type TasksResponse } from "../process"
import { fiscalChip, fiscalYears, YearPicker } from "@/components/period-picker"
import { monthLabel, NumberCell, SaveBar, sheet, SheetTable, sumDisplay, useEdits, useUnsavedGuard } from "@/components/spreadsheet"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

type Cell = { year: number; month: number; value: string }
type SheetData = {
  fiscalYear: number
  months: Array<{ year: number; month: number; label: string; open: boolean; approved: boolean; approvedBy: string | null; closed: boolean }>
  people: Array<{ id: number; nameFa: string; division: string | null; divisionId: number | null; endsAt: { year: number; month: number } | null; values: Cell[]; defaults: Cell[] }>
  divisions: Array<{ id: number; nameFa: string }>
  otherItems: Array<{ id: number; labelFa: string; values: Cell[] }>
  allocation: Array<{ year: number; month: number; units: Array<{ id: number; nameFa: string; personnel: string; other: string }>; unallocated: Array<{ nameFa: string; amount: string }> }>
}

const sKey = (id: number, year: number, month: number) => `s|${id}|${year}|${month}`
const oKey = (id: number, year: number, month: number) => `o|${id}|${year}|${month}`

/** CFO: each person's monthly pay + «سایر هزینه‌ها» rows, approved one month at a time. */
export function PayrollPage() {
  const client = useQueryClient()
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const period = tasks.data?.cycle.period
  const defaultFy = period ? (period.month >= 7 ? period.year : period.year - 1) : null
  const [fy, setFy] = useState<number | null>(null)
  useEffect(() => { if (fy === null && defaultFy) setFy(defaultFy) }, [defaultFy, fy])
  const data = useQuery({ queryKey: ["payroll-sheet", fy], queryFn: () => api<SheetData>(`/payroll/sheet/${fy}`), enabled: fy !== null })

  const original = useMemo(() => {
    if (!data.data) return undefined
    const map = new Map<string, string>()
    for (const person of data.data.people) for (const cell of person.values) map.set(sKey(person.id, cell.year, cell.month), cell.value)
    for (const item of data.data.otherItems) for (const cell of item.values) map.set(oKey(item.id, cell.year, cell.month), cell.value)
    return map
  }, [data.data])
  const edits = useEdits(original)
  const guard = useUnsavedGuard(edits.dirty)
  const refresh = () => Promise.all(["payroll-sheet", "process-tasks", "process-status", "fpa", "pnl-fiscal-year", "finance-unit"].map((key) => client.invalidateQueries({ queryKey: [key] })))

  const save = useMutation({
    mutationFn: () => {
      const salaries: unknown[] = []; const others: unknown[] = []
      for (const [key, value] of edits.entries()) {
        const [kind, id, year, month] = key.split("|")
        const cell = { year: Number(year), month: Number(month), value: value === "" ? null : value }
        if (kind === "s") salaries.push({ employeeId: Number(id), ...cell }); else others.push({ itemId: Number(id), ...cell })
      }
      return api("/payroll/sheet", { method: "PUT", body: JSON.stringify({ salaries, others }) })
    },
    onSuccess: refresh,
  })
  const action = useMutation({
    mutationFn: ({ method = "POST", path, body }: { method?: "POST" | "PATCH" | "DELETE"; path: string; body?: unknown }) => api(path, { method, body: body === undefined ? undefined : JSON.stringify(body) }),
    onSuccess: refresh,
  })
  const [newRow, setNewRow] = useState("")
  const [approveTarget, setApproveTarget] = useState<SheetData["months"][number] | null>(null)
  const [unapproveTarget, setUnapproveTarget] = useState<SheetData["months"][number] | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SheetData["otherItems"][number] | null>(null)
  const [personDialog, setPersonDialog] = useState<PersonDialogState | null>(null)

  if (!data.data || fy === null) return <Page title="حقوق و سایر هزینه‌ها"><LoadingCard /></Page>
  const { months, people, otherItems, allocation, divisions } = data.data
  const locked = (month: SheetData["months"][number]) => month.approved || month.closed || !month.open
  // From a person's end month on there is no salary: the cell is locked and the row is faded.
  const left = (person: SheetData["people"][number], month: { year: number; month: number }) => person.endsAt !== null && month.year * 12 + month.month >= person.endsAt.year * 12 + person.endsAt.month
  // Why a month is locked, in the user's words — shown as the cell tooltip.
  const lockTitle = (month: SheetData["months"][number]) => month.approved ? "تأیید شده" : month.closed ? "دوره بسته" : "ماه آینده"
  const defaultOf = new Map(people.flatMap((person) => person.defaults.map((cell) => [sKey(person.id, cell.year, cell.month), cell.value] as const)))
  const effective = (key: string) => Number(edits.get(key) || defaultOf.get(key) || 0)
  const payrollTotal = (month: SheetData["months"][number]) => people.reduce((sum, person) => sum + effective(sKey(person.id, month.year, month.month)), 0)
  const otherTotal = (month: SheetData["months"][number]) => otherItems.reduce((sum, item) => sum + Number(edits.get(oKey(item.id, month.year, month.month)) || 0), 0)
  const allocationOf = new Map(allocation.map((row) => [`${row.year}-${row.month}`, row]))
  const unitNames = [...new Map(allocation.flatMap((row) => row.units.map((unit) => [unit.id, unit.nameFa] as const))).entries()]
  const pasteInto = (keys: (rowIndex: number, monthIndex: number) => string | null, row: number, col: number) => (matrix: string[][]) =>
    edits.setMany(matrix.flatMap((cells, i) => cells.map((cell, j): [string, string] | null => { const key = keys(row + i, col + j); return key ? [key, cell] : null }).filter((entry): entry is [string, string] => entry !== null)))
  const salaryKeyAt = (rowIndex: number, monthIndex: number) => { const person = people[rowIndex]; const month = months[monthIndex]; return person && month && !locked(month) && !left(person, month) ? sKey(person.id, month.year, month.month) : null }
  const otherKeyAt = (rowIndex: number, monthIndex: number) => { const item = otherItems[rowIndex]; const month = months[monthIndex]; return item && month && !locked(month) ? oKey(item.id, month.year, month.month) : null }

  return (
    <Page title="حقوق و سایر هزینه‌ها" description="دریافتی ماهانه هر نفر و سایر هزینه‌های شرکت را مثل اکسل وارد کنید و هر ماه را جداگانه تأیید کنید. ارقام به میلیون ریال هستند.">
      <YearPicker years={fiscalYears(defaultFy ?? fy)} value={fy} onChange={(next) => guard(() => setFy(next))} label={fiscalChip} ariaLabel="سال مالی" />
      {action.error && <p role="alert" className="text-sm text-destructive">{messageFor(action.error)}</p>}

      <Card>
        <CardContent className="pt-6">
          <SheetTable label="حقوق و سایر هزینه‌ها">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className={cn(sheet.corner, "min-w-56")} rowSpan={2}>نام</th>
                {months.map((month) => <th key={`${month.year}-${month.month}`} className={cn(sheet.th, "min-w-24", !month.open && "text-neutral-500")}>{monthLabel(month.year, month.month)}</th>)}
                <th className={cn(sheet.th, "min-w-24")} rowSpan={2}>جمع</th>
              </tr>
              <tr>
                {months.map((month) => (
                  <th key={`${month.year}-${month.month}`} className={cn(sheet.th, "px-1 py-1 font-normal")}>
                    {month.approved ? (
                      <span className="flex flex-col items-center gap-0.5">
                        <span className="inline-flex items-center gap-1 text-xs text-success"><Check className="size-3" aria-hidden /> تأیید شد</span>
                        {!month.closed && <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline" disabled={action.isPending} onClick={() => setUnapproveTarget(month)}>بازگشایی</button>}
                        {month.closed && <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Lock className="size-2.5" aria-hidden /> دوره بسته</span>}
                      </span>
                    ) : month.open ? (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={edits.dirty > 0 || action.isPending}
                        title={edits.dirty ? "ابتدا تغییرات را ذخیره کنید" : undefined}
                        onClick={() => setApproveTarget(month)}>
                        تأیید ماه
                      </Button>
                    ) : <span className="text-xs text-neutral-500">آینده</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr><th colSpan={months.length + 2} className={sheet.section}>حقوق و دستمزد</th></tr>
              {people.map((person, rowIndex) => (
                <Fragment key={person.id}>
                {(rowIndex === 0 || people[rowIndex - 1]!.division !== person.division) && (
                  <tr>
                    <th colSpan={months.length + 2} className="border border-neutral-300 bg-neutral-50 p-0 text-right dark:bg-muted/40">
                      <span className="sticky right-0 flex w-fit items-center gap-2 px-3 py-1">
                        <span className="text-xs font-semibold text-success">{person.division ? `واحد ${person.division}` : "بدون واحد"}</span>
                        <button type="button" disabled={edits.dirty > 0 || action.isPending} title={edits.dirty ? "ابتدا تغییرات را ذخیره کنید" : undefined}
                          onClick={() => setPersonDialog({ mode: "add", divisionId: person.divisionId })}
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-normal text-muted-foreground hover:bg-background hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50">
                          <UserPlus className="size-3.5" aria-hidden /> افزودن فرد
                        </button>
                      </span>
                    </th>
                  </tr>
                )}
                <tr className={cn(person.endsAt && "text-neutral-500 [&_td]:opacity-60")}>
                  <th scope="row" className={cn(sheet.rowHead, "p-0")}>
                    <span className="flex min-h-7 items-center gap-1 ps-3 pe-1">
                      <span className={cn("min-w-0 flex-1 truncate", person.endsAt && "text-neutral-500")}>{person.nameFa}</span>
                      {person.endsAt && <span className="shrink-0 rounded-full bg-neutral-100 px-1.5 py-px text-xs text-neutral-600" title={`همکاری از ${monthLabel(person.endsAt.year, person.endsAt.month)} قطع شده است`}>تا {monthLabel(...previousOf(person.endsAt))}</span>}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" aria-label={`کارهای ردیف ${person.nameFa}`} disabled={edits.dirty > 0 || action.isPending} title={edits.dirty ? "ابتدا تغییرات را ذخیره کنید" : undefined}
                            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-40">
                            <MoreHorizontal className="size-3.5" aria-hidden />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setPersonDialog({ mode: "move", person })}><ArrowLeftRight className="size-4" aria-hidden /> انتقال به واحد دیگر…</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {person.endsAt
                            ? <DropdownMenuItem onSelect={() => action.mutate({ method: "PATCH", path: `/payroll/people/${person.id}`, body: { end: null } })}><UserRoundCheck className="size-4" aria-hidden /> بازگشت به همکاری</DropdownMenuItem>
                            : <DropdownMenuItem onSelect={() => setPersonDialog({ mode: "end", person })}><UserMinus className="size-4" aria-hidden /> پایان همکاری…</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </span>
                  </th>
                  {months.map((month, col) => {
                    const key = sKey(person.id, month.year, month.month)
                    const gone = left(person, month)
                    const isLocked = locked(month) || gone
                    return (
                      <td key={key} className={sheet.td}>
                        <NumberCell grid="salary" row={rowIndex} col={col} value={edits.get(key)} hint={defaultOf.get(key)} edited={edits.isEdited(key)} disabled={isLocked}
                          label={`${person.nameFa} — ${month.label}`} title={gone ? "پس از پایان همکاری" : isLocked ? lockTitle(month) : undefined}
                          onChange={(value) => edits.set(key, value)} onPasteGrid={pasteInto(salaryKeyAt, rowIndex, col)} />
                      </td>
                    )
                  })}
                  <td className={sheet.total}>{sumDisplay(months.reduce((sum, month) => sum + effective(sKey(person.id, month.year, month.month)), 0))}</td>
                </tr>
                </Fragment>
              ))}
              <tr>
                <th scope="row" className={cn(sheet.rowHead, "bg-neutral-50 font-semibold")}>جمع حقوق</th>
                {months.map((month) => <td key={`${month.year}-${month.month}`} className={sheet.total}>{sumDisplay(payrollTotal(month))}</td>)}
                <td className={sheet.total}>{sumDisplay(months.reduce((sum, month) => sum + payrollTotal(month), 0))}</td>
              </tr>

              <tr><th colSpan={months.length + 2} className={sheet.section}>سایر هزینه‌ها <span className="font-normal text-muted-foreground">— جمع هر ماه به‌طور مساوی بین کسب‌وکارها تقسیم می‌شود</span></th></tr>
              {otherItems.map((item, rowIndex) => (
                <tr key={item.id}>
                  <th scope="row" className={cn(sheet.rowHead, "p-0")}>
                    <div className="flex items-center">
                      <input defaultValue={item.labelFa} aria-label="عنوان ردیف" title="Enter ذخیره، Escape انصراف" className="h-7 min-w-0 flex-1 bg-transparent px-3 outline-none focus:outline-2 focus:outline-offset-[-2px] focus:outline-success"
                        onBlur={(event) => { const labelFa = event.target.value.trim(); if (labelFa && labelFa !== item.labelFa) action.mutate({ method: "PATCH", path: `/payroll/other-items/${item.id}`, body: { labelFa } }) }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") event.currentTarget.blur()
                          else if (event.key === "Escape") { event.currentTarget.value = item.labelFa; event.currentTarget.blur() }
                        }} />
                      <button type="button" aria-label={`حذف ردیف ${item.labelFa}`} className="rounded px-2 py-1 text-muted-foreground hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none" disabled={action.isPending}
                        onClick={() => setDeleteTarget(item)}>
                        <Trash2 className="size-3.5" aria-hidden />
                      </button>
                    </div>
                  </th>
                  {months.map((month, col) => {
                    const key = oKey(item.id, month.year, month.month)
                    const isLocked = locked(month)
                    return (
                      <td key={key} className={sheet.td}>
                        <NumberCell grid="other" row={rowIndex} col={col} value={edits.get(key)} edited={edits.isEdited(key)} disabled={isLocked}
                          label={`${item.labelFa} — ${month.label}`} title={isLocked ? lockTitle(month) : undefined}
                          onChange={(value) => edits.set(key, value)} onPasteGrid={pasteInto(otherKeyAt, rowIndex, col)} />
                      </td>
                    )
                  })}
                  <td className={sheet.total}>{sumDisplay(months.reduce((sum, month) => sum + Number(edits.get(oKey(item.id, month.year, month.month)) || 0), 0))}</td>
                </tr>
              ))}
              <tr>
                <th scope="row" className={cn(sheet.rowHead, "p-1")} colSpan={1}>
                    <form className="flex items-center gap-1" onSubmit={(event) => { event.preventDefault(); if (newRow.trim()) action.mutate({ path: "/payroll/other-items", body: { labelFa: newRow.trim() } }, { onSuccess: () => setNewRow("") }) }}>
                      <Input value={newRow} onChange={(event) => setNewRow(event.target.value)} placeholder="ردیف جدید، مثلاً اجاره" className="h-7 text-xs" aria-label="عنوان ردیف جدید" />
                      <Button type="submit" size="sm" variant="ghost" className="h-7 px-2" disabled={!newRow.trim() || action.isPending}><Plus className="size-4" aria-hidden /> ردیف</Button>
                    </form>
                </th>
                <td colSpan={months.length + 1} className="border border-neutral-300 bg-neutral-50/50" />
              </tr>
              <tr>
                <th scope="row" className={cn(sheet.rowHead, "bg-neutral-50 font-semibold")}>جمع سایر هزینه‌ها</th>
                {months.map((month) => <td key={`${month.year}-${month.month}`} className={sheet.total}>{sumDisplay(otherTotal(month))}</td>)}
                <td className={sheet.total}>{sumDisplay(months.reduce((sum, month) => sum + otherTotal(month), 0))}</td>
              </tr>
              <tr>
                <th scope="row" className={cn(sheet.rowHead, "bg-neutral-100 font-bold")}>جمع کل هزینه</th>
                {months.map((month) => <td key={`${month.year}-${month.month}`} className={cn(sheet.total, "bg-neutral-100 font-bold")}>{sumDisplay(payrollTotal(month) + otherTotal(month))}</td>)}
                <td className={cn(sheet.total, "bg-neutral-100 font-bold")}>{sumDisplay(months.reduce((sum, month) => sum + payrollTotal(month) + otherTotal(month), 0))}</td>
              </tr>
            </tbody>
          </SheetTable>
          <p className="mt-2 text-xs text-muted-foreground">خانه‌های نقطه‌چین، حقوق ماه قبل را نشان می‌دهند؛ با «تأیید ماه» همین ارقام ثبت می‌شوند مگر اینکه عدد دیگری بنویسید. ماه‌های تأییدشده فقط خواندنی‌اند (نشان «تأیید شده»، «دوره بسته» یا «ماه آینده» را ببینید). Enter و کلیدهای جهت بین خانه‌ها حرکت می‌کنند و می‌توانید از اکسل کپی و جای‌گذاری کنید.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">تقسیم هزینه‌ها بین کسب‌وکارها</CardTitle>
          <CardDescription>برای ماه‌های تأییدشده: حقوق به نسبت تخصیص زمانی تأییدشده هر نفر + سهم مساوی از سایر هزینه‌ها. حقوق افرادی که نسبت کاری تأییدشده ندارند جداگانه نمایش داده می‌شود.</CardDescription>
        </CardHeader>
        <CardContent>
          {!allocation.length ? <p className="text-sm text-muted-foreground">هنوز ماهی تأیید نشده است.</p> : (
            <>
              <SheetTable label="تقسیم هزینه‌ها بین کسب‌وکارها">
                <thead>
                  <tr>
                    <th className={cn(sheet.corner, "min-w-56")}>کسب‌وکار</th>
                    {months.map((month) => <th key={`${month.year}-${month.month}`} className={cn(sheet.th, "min-w-24")}>{monthLabel(month.year, month.month)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {unitNames.map(([id, nameFa]) => (
                    <tr key={id}>
                      <th scope="row" className={sheet.rowHead}>{nameFa}</th>
                      {months.map((month) => {
                        const unit = allocationOf.get(`${month.year}-${month.month}`)?.units.find((item) => item.id === id)
                        return (
                          <td key={`${month.year}-${month.month}`} className="border border-neutral-300 px-2 py-1 text-center tabular-nums" title={unit ? undefined : "در انتظار تأیید ماه"}>
                            {unit ? (
                              <span className="grid leading-tight">
                                <span className="font-medium">{sumDisplay(Number(unit.personnel) + Number(unit.other))}</span>
                                <span className="text-xs text-neutral-500">حقوق {fa(unit.personnel)}، سایر {fa(unit.other)}</span>
                              </span>
                            ) : <span className="text-neutral-500">—</span>}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                  <tr>
                    <th scope="row" className={cn(sheet.rowHead, "text-amber-800")}>تخصیص‌نیافته</th>
                    {months.map((month) => {
                      const rows = allocationOf.get(`${month.year}-${month.month}`)?.unallocated
                      return <td key={`${month.year}-${month.month}`} className="border border-neutral-300 px-2 py-1 text-center tabular-nums text-amber-800" title={rows?.map((row) => `${row.nameFa}: ${fa(row.amount)}`).join("\n") ?? "در انتظار تأیید ماه"}>{rows ? sumDisplay(rows.reduce((sum, row) => sum + Number(row.amount), 0)) : <span className="text-neutral-500">—</span>}</td>
                    })}
                  </tr>
                </tbody>
              </SheetTable>
              <p className="mt-2 text-xs text-muted-foreground">هر خانه: جمع ماه (درشت) از حقوق تخصیص‌یافته و سهم مساوی سایر هزینه‌ها؛ «—» یعنی ماه هنوز تأیید نشده است.</p>
            </>
          )}
          {allocation.length > 0 && <Button variant="ghost" size="sm" className="mt-2" disabled={action.isPending} onClick={() => allocation.forEach((row) => action.mutate({ path: "/payroll/recompute", body: { year: row.year, month: row.month } }))}><RotateCcw className="size-4" aria-hidden /> بازمحاسبه</Button>}
          <ConfirmDialog
            open={approveTarget !== null} onOpenChange={(next) => { if (!next) setApproveTarget(null) }}
            title={`تأیید ${approveTarget?.label ?? ""}`} description={`حقوق و سایر هزینه‌های ${approveTarget?.label ?? ""} تأیید و بین کسب‌وکارها تقسیم شود؟ حقوق ثبت‌نشده از ماه قبل برداشته می‌شود.`}
            confirmLabel="تأیید ماه" pending={action.isPending}
            onConfirm={() => { if (approveTarget) action.mutate({ path: "/payroll/approve", body: { year: approveTarget.year, month: approveTarget.month } }, { onSuccess: () => setApproveTarget(null) }) }}
          />
          <ConfirmDialog
            open={unapproveTarget !== null} onOpenChange={(next) => { if (!next) setUnapproveTarget(null) }}
            title={`بازگشایی ${unapproveTarget?.label ?? ""}`} description={`تأیید ${unapproveTarget?.label ?? ""} برداشته شود؟ ارقام این ماه دوباره قابل ویرایش می‌شود.`}
            confirmLabel="بازگشایی" pending={action.isPending}
            onConfirm={() => { if (unapproveTarget) action.mutate({ path: "/payroll/unapprove", body: { year: unapproveTarget.year, month: unapproveTarget.month } }, { onSuccess: () => setUnapproveTarget(null) }) }}
          />
          {personDialog && (
            <PersonDialog state={personDialog} divisions={divisions} months={months} pending={action.isPending} error={action.error}
              onClose={() => { action.reset(); setPersonDialog(null) }}
              onSubmit={(request) => action.mutate(request, { onSuccess: () => setPersonDialog(null) })} />
          )}
          <ConfirmDialog
            open={deleteTarget !== null} onOpenChange={(next) => { if (!next) setDeleteTarget(null) }}
            title={`حذف ردیف «${deleteTarget?.labelFa ?? ""}»`} description={`ردیف «${deleteTarget?.labelFa ?? ""}» حذف شود؟ ارقام ماه‌های تأییدنشده آن پاک می‌شود.`}
            confirmLabel="حذف ردیف" danger pending={action.isPending}
            onConfirm={() => { if (deleteTarget) action.mutate({ method: "DELETE", path: `/payroll/other-items/${deleteTarget.id}` }, { onSuccess: () => setDeleteTarget(null) }) }}
          />
        </CardContent>
      </Card>

      <SaveBar edits={edits} save={save} />
    </Page>
  )
}

const previousOf = (period: { year: number; month: number }): [number, number] => (period.month === 1 ? [period.year - 1, 12] : [period.year, period.month - 1])

type Person = SheetData["people"][number]
type PersonDialogState = { mode: "add"; divisionId: number | null } | { mode: "move"; person: Person } | { mode: "end"; person: Person }
type Request = { method?: "POST" | "PATCH" | "DELETE"; path: string; body?: unknown }
const NONE = "none"

/** Add someone to a unit, move them to another unit, or record the month their employment ended. */
function PersonDialog({ state, divisions, months, pending, error, onClose, onSubmit }: {
  state: PersonDialogState; divisions: SheetData["divisions"]; months: SheetData["months"]; pending: boolean; error: unknown
  onClose: () => void; onSubmit: (request: Request) => void
}) {
  const [nameFa, setNameFa] = useState("")
  const [division, setDivision] = useState(String((state.mode === "add" ? state.divisionId : state.person.divisionId) ?? NONE))
  const [end, setEnd] = useState(() => { const open = months.find((month) => month.open && !month.approved && !month.closed) ?? months.at(-1)!; return `${open.year}-${open.month}` })
  const divisionId = division === NONE ? null : Number(division)
  const title = state.mode === "add" ? "افزودن فرد به حقوق" : state.mode === "move" ? `انتقال ${state.person.nameFa}` : `پایان همکاری با ${state.person.nameFa}`
  const submit = () => {
    if (state.mode === "add") onSubmit({ path: "/payroll/people", body: { nameFa: nameFa.trim(), divisionId } })
    else if (state.mode === "move") onSubmit({ method: "PATCH", path: `/payroll/people/${state.person.id}`, body: { divisionId } })
    else { const [year, month] = end.split("-").map(Number); onSubmit({ method: "PATCH", path: `/payroll/people/${state.person.id}`, body: { end: { year, month } } }) }
  }
  const unitPicker = (
    <Field>
      <FieldLabel>واحد</FieldLabel>
      <Select value={division} onValueChange={setDivision}>
        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          {divisions.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.nameFa}</SelectItem>)}
          <SelectItem value={NONE}>بدون واحد</SelectItem>
        </SelectContent>
      </Select>
    </Field>
  )
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !pending) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {state.mode === "add" ? "این فرد بلافاصله در جدول حقوق نمایش داده می‌شود. برای ورود به سامانه، مدیر سامانه از صفحه کاربران برایش رمز صادر می‌کند."
              : state.mode === "move" ? "از این پس در جدول حقوق و تأیید نسبت کاری زیر واحد جدید قرار می‌گیرد. ارقام ثبت‌شده تغییری نمی‌کند."
              : "ردیف در جدول کمرنگ می‌ماند. از ماه انتخاب‌شده به بعد حقوقی ثبت یا از ماه قبل کپی نمی‌شود و نسبت کاری از او خواسته نمی‌شود. ماه‌های قبل دست‌نخورده می‌مانند."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); submit() }}>
          <FieldGroup>
            {state.mode === "add" && (
              <Field>
                <FieldLabel htmlFor="person-name">نام و نام خانوادگی</FieldLabel>
                <Input id="person-name" value={nameFa} onChange={(event) => setNameFa(event.target.value)} required minLength={2} maxLength={200} autoFocus />
              </Field>
            )}
            {state.mode !== "end" && unitPicker}
            {state.mode === "end" && (
              <Field>
                <FieldLabel>همکاری از کدام ماه قطع شد؟</FieldLabel>
                <Select value={end} onValueChange={setEnd}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {months.map((month) => <SelectItem key={`${month.year}-${month.month}`} value={`${month.year}-${month.month}`}>از {month.label}{month.approved ? " (تأییدشده)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FieldDescription>اولین ماهی که دیگر حقوق نمی‌گیرد. اگر ماه انتخابی تأیید شده باشد، حقوق ثبت‌شده آن ماه باقی می‌ماند.</FieldDescription>
              </Field>
            )}
            {error ? <FieldError role="alert">{messageFor(error)}</FieldError> : null}
            <DialogFooter>
              <Button type="submit" variant={state.mode === "end" ? "destructive" : "default"} disabled={pending || (state.mode === "add" && nameFa.trim().length < 2)}>
                {pending ? "در حال ثبت…" : state.mode === "add" ? "افزودن" : state.mode === "move" ? "انتقال" : "ثبت پایان همکاری"}
              </Button>
              <Button type="button" variant="ghost" disabled={pending} onClick={onClose}>انصراف</Button>
            </DialogFooter>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  )
}
